import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  AlertTriangle, Heart, Bitcoin, Briefcase, Smartphone, Globe, Mail,
  TrendingUp, CreditCard, Shield, ArrowRight, Search, FileText, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const scamCategories = [
  {
    slug: 'romance-scams',
    title: 'Romance Scams',
    icon: Heart,
    color: 'pink',
    shortDesc: 'Fraudsters create fake profiles on dating sites to build emotional connections and steal money.',
    warningCount: 2340,
    recentReports: 156
  },
  {
    slug: 'crypto-investment-scams',
    title: 'Crypto Investment Scams',
    icon: Bitcoin,
    color: 'orange',
    shortDesc: 'Fake cryptocurrency platforms and investment schemes promising unrealistic returns.',
    warningCount: 4521,
    recentReports: 312
  },
  {
    slug: 'job-offer-scams',
    title: 'Job Offer Scams',
    icon: Briefcase,
    color: 'blue',
    shortDesc: 'Fake job listings, recruitment scams, and employment fraud targeting job seekers.',
    warningCount: 1876,
    recentReports: 98
  },
  {
    slug: 'whatsapp-scams',
    title: 'WhatsApp Scams',
    icon: Smartphone,
    color: 'green',
    shortDesc: 'Impersonation scams, fake offers, and phishing messages via WhatsApp.',
    warningCount: 3245,
    recentReports: 234
  },
  {
    slug: 'fake-company-scams',
    title: 'Fake Company Scams',
    icon: Globe,
    color: 'purple',
    shortDesc: 'Fraudulent businesses, fake online stores, and non-existent companies.',
    warningCount: 2987,
    recentReports: 187
  },
  {
    slug: 'phishing-websites',
    title: 'Phishing Websites',
    icon: Mail,
    color: 'red',
    shortDesc: 'Fake websites designed to steal login credentials and personal information.',
    warningCount: 5632,
    recentReports: 421
  },
  {
    slug: 'investment-fraud',
    title: 'Investment Fraud',
    icon: TrendingUp,
    color: 'yellow',
    shortDesc: 'Ponzi schemes, fake trading platforms, and fraudulent investment opportunities.',
    warningCount: 3456,
    recentReports: 267
  },
  {
    slug: 'loan-scams',
    title: 'Loan Scams',
    icon: CreditCard,
    color: 'cyan',
    shortDesc: 'Advance fee fraud, fake lenders, and loan modification scams.',
    warningCount: 1654,
    recentReports: 89
  }
];

const ScamDatabasePage = () => {
  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>Scam Database - Types of Scams & Warning Signs | ActionFraudOne</title>
        <meta name="description" content="Browse our comprehensive scam database. Learn about romance scams, crypto fraud, job scams, phishing, and more. Protect yourself with our fraud intelligence." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Scam Database",
            "description": "Comprehensive database of scam types, warning signs, and prevention advice",
            "publisher": {
              "@type": "Organization",
              "name": "ActionFraudOne"
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-blue-400">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-blue-400">Fraud Database</span>
        </nav>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e40af]/10 border border-[#1e40af]/30 rounded-full mb-6">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Fraud Intelligence Database</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Fraud Database
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Browse our comprehensive database of scam types. Learn warning signs, typical tactics, 
            and how to protect yourself from fraud.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
          <Card className="bg-[#0d1d32] border-[#1e40af]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">25K+</p>
              <p className="text-gray-500 text-sm">Scam Reports</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d32] border-[#1e40af]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">8</p>
              <p className="text-gray-500 text-sm">Scam Categories</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d32] border-[#1e40af]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">1,800+</p>
              <p className="text-gray-500 text-sm">This Month</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d32] border-[#1e40af]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">24/7</p>
              <p className="text-gray-500 text-sm">Monitoring</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Check CTA */}
        <Card className="bg-gradient-to-r from-[#1e40af]/20 to-cyan-500/10 border-[#1e40af]/30 mb-12">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Search className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Check Something Specific?</h3>
                  <p className="text-gray-400">Enter any website, company, phone, or email to get instant fraud analysis.</p>
                </div>
              </div>
              <Link to="/">
                <Button className="bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold px-6">
                  Check Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Scam Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {scamCategories.map((category) => (
            <Link key={category.slug} to={`/scams/${category.slug}`}>
              <Card className="bg-[#0d1d32] border-[#1e40af]/20 hover:border-[#1e40af]/40 h-full group cursor-pointer transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 bg-${category.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <category.icon className={`w-6 h-6 text-${category.color}-400`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                        {category.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-gray-500 text-xs">{category.warningCount.toLocaleString()} warnings</span>
                        <span className="text-cyan-400 text-xs">+{category.recentReports} this week</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{category.shortDesc}</p>
                  <div className="flex items-center text-blue-400 text-sm font-medium">
                    Learn More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Report a Scam CTA */}
        <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
          <CardContent className="p-6 md:p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Been Scammed or Spotted a Scam?</h3>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Help protect others by reporting suspicious activity. Your report contributes to our 
              fraud intelligence database and helps warn potential victims.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/report-a-scam">
                <Button className="bg-red-500 hover:bg-red-600 text-white font-bold px-6">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Report a Scam
                </Button>
              </Link>
              <Link to="/scam-check">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Shield className="w-4 h-4 mr-2" />
                  Check if You're at Risk
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: 'How do I know if something is a scam?',
                a: 'Common warning signs include unsolicited contact, pressure to act quickly, requests for upfront payment, promises of guaranteed returns, and requests for personal/financial information. Use our free scam checker to verify suspicious entities.'
              },
              {
                q: 'What should I do if I\'ve been scammed?',
                a: 'Contact your bank immediately if you\'ve sent money. Report to Action Fraud (UK) or your local police. Gather all evidence including messages, emails, and transaction records. Consider using our investigation service for detailed documentation.'
              },
              {
                q: 'How can I report a scam?',
                a: 'Use our Report a Scam form to submit details. Your report helps us update our database and warn others. You can also report directly to Action Fraud or the FCA if it involves financial services.'
              },
              {
                q: 'Are your scam checks confidential?',
                a: 'Yes, all submissions are handled with strict confidentiality. We do not share your personal information with third parties. Our systems are encrypted and secure.'
              }
            ].map((faq, index) => (
              <Card key={index} className="bg-[#0d1d32] border-[#1e40af]/20">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                  <p className="text-gray-500 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamDatabasePage;
